// ============================================================================
// components/remote/RemoteActionBar.tsx — панель действий сеанса «Удалённого
// доступа»: управление (запрос/разрешение/стоп), файлы устройства, показ
// экрана, завершение. Вынесена из RemotePanel (SRP, ≤400 строк на файл).
// «Ссылка для гостя» — УПРАВЛЕНИЕ (сервер: remote_manage/админ): без роли
// кнопка скрыта (SSoT доступа — сервер, RemoteService.CreateRemoteGuestLink).
// ============================================================================

import { FolderOpen, Link2, Maximize, Minimize, Monitor, MonitorOff, MousePointerClick, PhoneOff, ShieldCheck, ShieldOff } from 'lucide-react';
import type { RemoteRole } from '@/lib/remote/remoteSession';
import { REMOTE_QUALITY_PRESETS } from '@/lib/remote/remoteSession';
import type { RemoteControlApi } from '@/components/remote/useRemoteControl';
import type { RemoteFilesApi } from '@/components/remote/useRemoteFiles';
import { canManageRemote, useSecurityAccess } from '@/lib/securityRoles';

export type RemoteStatus = 'connecting' | 'waiting' | 'live' | 'error';

interface Props {
  role: RemoteRole;
  status: RemoteStatus;
  sharing: boolean;
  control: RemoteControlApi;
  files: RemoteFilesApi;
  filesPanelOpen: boolean;
  onToggleFilesPanel: () => void;
  preset: string;
  onPresetChange: (code: string) => void;
  onShareNow: () => void;
  onStopShare: () => void;
  onGuestLink: () => void;
  guestLinkBusy: boolean;
  onLeave: () => void;
}

const btn = 'flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium';

export function RemoteActionBar({
  role, status, sharing, control, files, filesPanelOpen, onToggleFilesPanel, preset, onPresetChange, onShareNow, onStopShare, onGuestLink, guestLinkBusy, onLeave,
}: Props) {
  // «Ссылка для гостя» — управление (remote_manage): без роли кнопка скрыта.
  const access = useSecurityAccess();
  const canManage = canManageRemote(access);
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 border-t border-white/10 px-4 py-4">
      {role === 'viewer' && control.controlOn && (
        <div className="basis-full text-center text-[11px] text-white/60 -mt-1 mb-1">
          Управление включено · Захват мыши: {control.pointerLocked ? 'вкл (клик по экрану)' : 'выкл — кликните по экрану'}
          {' · '}Системные клавиши: {control.keyboardLocked ? 'вкл' : control.fullscreen ? 'вкл при клике' : 'нужен полный экран'}
          {' · '}Esc — выйти из захвата
        </div>
      )}
      {role === 'viewer' && !control.controlOn && status === 'live' && (
        <button type="button" onClick={control.requestControl} className={`${btn} bg-white/10 hover:bg-white/20`}>
          <MousePointerClick className="h-5 w-5" />
          Запросить управление
        </button>
      )}
      {role === 'host' && control.controlRequested && !control.controlOn && (
        <>
          <button type="button" onClick={control.allowControl} className={`${btn} bg-green-600 hover:bg-green-500`}>
            <ShieldCheck className="h-5 w-5" />
            Разрешить управление
          </button>
          <button type="button" onClick={control.denyControl} className={`${btn} bg-white/10 hover:bg-white/20`}>
            <ShieldOff className="h-5 w-5" />
            Запретить
          </button>
        </>
      )}
      {role === 'host' && control.controlOn && (
        <button type="button" onClick={control.stopControl} className={`${btn} bg-amber-600 hover:bg-amber-500`}>
          <ShieldOff className="h-5 w-5" />
          Остановить управление
        </button>
      )}

      {role === 'viewer' && (files.filesOn ? (
        <button type="button" onClick={onToggleFilesPanel} className={`${btn} ${filesPanelOpen ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20'}`}>
          <FolderOpen className="h-5 w-5" />
          Файлы устройства
        </button>
      ) : (
        <button type="button" onClick={files.requestFiles} className={`${btn} bg-white/10 hover:bg-white/20`}>
          <FolderOpen className="h-5 w-5" />
          Запросить файлы
        </button>
      ))}
      {role === 'host' && files.filesRequested && !files.filesOn && (
        <>
          <button type="button" onClick={files.enableFiles} className={`${btn} bg-green-600 hover:bg-green-500`}>
            <FolderOpen className="h-5 w-5" />
            Разрешить файлы
          </button>
          <button type="button" onClick={files.denyFiles} className={`${btn} bg-white/10 hover:bg-white/20`}>
            <ShieldOff className="h-5 w-5" />
            Запретить
          </button>
        </>
      )}
      {role === 'host' && files.filesOn && (
        <button type="button" onClick={files.disableFiles} className={`${btn} bg-amber-600 hover:bg-amber-500`}>
          <ShieldOff className="h-5 w-5" />
          Остановить доступ к файлам
        </button>
      )}

      {role === 'host' && (
        <select
          value={preset}
          onChange={(e) => onPresetChange(e.target.value)}
          title="Качество показа: fps применяется сразу; битрейт/разрешение — при следующем начале показа"
          className="rounded-full border border-white/15 bg-black/40 px-3 py-2 text-xs text-white outline-none focus:border-primary"
        >
          {REMOTE_QUALITY_PRESETS.map((p) => (
            <option key={p.code} value={p.code}>{p.label}</option>
          ))}
        </select>
      )}
      {role === 'host' && canManage && (
        <button type="button" disabled={guestLinkBusy} onClick={onGuestLink} className={`${btn} bg-white/10 hover:bg-white/20 disabled:opacity-50`}>
          <Link2 className="h-5 w-5" />
          Ссылка для гостя
        </button>
      )}
      {role === 'host' && (sharing ? (
        <button type="button" onClick={onStopShare} className={`${btn} bg-amber-600 hover:bg-amber-500`}>
          <MonitorOff className="h-5 w-5" />
          Остановить показ
        </button>
      ) : (
        <button type="button" onClick={onShareNow} className={`${btn} bg-primary text-primary-foreground hover:opacity-90`}>
          <Monitor className="h-5 w-5" />
          Поделиться экраном
        </button>
      ))}
      {role === 'viewer' && status === 'live' && (
        <button type="button" onClick={control.toggleFullscreen} title="Полный экран (нужен для системных клавиш)" className={`${btn} bg-white/10 hover:bg-white/20`}>
          {control.fullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          {control.fullscreen ? 'Выйти из полного экрана' : 'Полный экран'}
        </button>
      )}
      <button type="button" onClick={onLeave} className={`${btn} bg-red-600 hover:bg-red-500`}>
        <PhoneOff className="h-5 w-5" />
        Завершить
      </button>
    </div>
  );
}
